import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Order } from "../../../interfaces/order";
import { getOrderByID } from "../../../services/orderService";
import {
  downloadPreFillTransferReleaseForm,
  getOrderFormTypes,
  uploadCandidateForm,
} from "../../../services/orderFormService";
import { getSimpleClient } from "../../../services/clientService";
import { getReferenceOrderByOrderID } from "../../../services/referenceOrderService";
import { TransferRelease } from "./TransferReleaseForm";
import { OrderFormType } from "../../../interfaces/orderFormType";

const TransferReleaseUpload = () => {
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const location = useLocation();
  const { orderID } = location.state || {};
  const [transferFormUrl, setTransferFormUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [transferRelease, setTransferRelease] = useState<TransferRelease>({
    re: "",
    ssn: "",
    requestingEntityName: "",
    requestingEntityTypedName: "",
    requestingEntitySignature: "",
    requestingEntityAuthorizedDate: new Date(),
    requestingEntityAddress: "",
    clergyTypedName: "",
    clergySignature: "",
    clergyWitnessName: "",
    clergyWitnessSignature: "",
    clergyDate: new Date(),
    clergySSN: "",
    authorizingEntityName: "",
    authorizingEntitySignature: "",
    authorizingEntityDate: new Date(),
    authorizingEntityAddress: "",
    authorizingEntityTypedName: "",
  });
  const [transferFormType, setTransferFormType] = useState<OrderFormType>();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);
        const client = await getSimpleClient(order.clientID);
        const referenceOrder = await getReferenceOrderByOrderID(order.orderID);

        setTransferRelease((prev) => ({
          ...prev,
          ssn: order.candidateSSN ? order.candidateSSN : "",
          re:
            order.candidateFirstName +
            " " +
            order.candidateMI +
            " " +
            order.candidateLastName,
          requestingEntityName: client.diocese
            ? client.diocese
            : client.clientName,
          authorizingEntityName: referenceOrder.priorClientName,
        }));

        const orderFormTypesResponse = await getOrderFormTypes();
        let transferFormType = orderFormTypesResponse?.find(
          (o) => o.orderFormTypeID === 8,
        );
        setTransferFormType(transferFormType);
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderID]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleDownloadForm = async () => {
    if (!order) return;

    try {
      setLoadingSpinner(true);
      const response = await downloadPreFillTransferReleaseForm(
        order,
        transferRelease,
      );
      if (response) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName = "Transfer_Form.pdf";
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return true;
      }
    } catch (error) {
      console.error("Error handling form download:", error);
      setErrorMessage("Error downloading candidate form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleFormUpload = async () => {
    if (!order || !transferFormType) return;

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf";
    fileInput.onchange = async (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setUploadedFile(file);
      const uploadedFileUrl = URL.createObjectURL(file);
      setTransferFormUrl(uploadedFileUrl);
      setSuccessMessage("Form ready for submission.");
    };
    fileInput.click();
  };

  const handleSave = async () => {
    if (!order || !transferFormType || !uploadedFile) {
      setErrorMessage("Please upload the form before submitting.");
      return;
    }

    try {
      setLoadingSpinner(true);
      const response = await uploadCandidateForm(
        order,
        transferFormType,
        uploadedFile,
      );
      if (response) {
        navigate("/candidate/needs-review", {
          state: { successMessage: "Form successfully submitted." },
        });
      } else {
        setErrorMessage("Upload failed.");
      }
    } catch (error) {
      console.error("Error uploading form on submit:", error);
      setErrorMessage("An error occurred during upload.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">TRANSFER RELEASE</div>

          <div className="data-container">
            <div className="text-sub-header">
              Authorization to Release/Transfer Confidential Information
            </div>
            <div className="text-body">
              Please download the Transfer Release Form by clicking the Download
              Form button. Completely and accurately fill out Section 2 (the
              middle section) titled Clergy/Subject Authorization to Release
              Information. When finished, upload the completed form by clicking
              the Upload Form button. Once uploaded, please click Submit to
              complete the process.{" "}
              <b>Please only upload PDF file types, no images.</b>
            </div>
            <div className="action-buttons">
              <button
                className="oxbtn btn-grey"
                onClick={() => handleDownloadForm()}
              >
                Download Form
              </button>
              <button
                className="oxbtn btn-grey"
                onClick={() => handleFormUpload()}
              >
                Upload Transfer Release Form
              </button>
              {transferFormUrl && (
                <a
                  href={transferFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  View Uploaded Form
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </a>
              )}
            </div>
          </div>
          <div className="btn-center">
            <button
              className={`oxbtn btn-green ${
                !transferFormUrl ? "disabled" : ""
              }`}
              onClick={() => handleSave()}
              disabled={!transferFormUrl}
            >
              Submit
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert-message alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default TransferReleaseUpload;
